// src/services/analytics.ts
import { getAnalytics, logEvent, isSupported } from 'firebase/analytics'
import { app } from '../lib/firebase-config'

// Minimal config - easy to disable everything
const ANALYTICS_CONFIG = {
  enabled: true, // Set to false to disable all analytics
  firebase: true,
  console: true, // Log events to console for debugging
}

// Core event types - EXPANDED for comprehensive tracking
type AnalyticsEvent = 
  // Session & Page tracking
  | 'session_start'
  | 'session_end'
  | 'page_view'
  
  // Activity tracking (Leap app)
  | 'activity_start' 
  | 'activity_complete'
  | 'struggle_moment'
  | 'success_moment'
  
  // Worksheet tracking (Teacher tools)
  | 'worksheet_generated'
  | 'worksheet_downloaded'
  | 'worksheet_preview_opened'
  
  // User behavior
  | 'feature_clicked'
  | 'upgrade_modal_shown'
  | 'upgrade_initiated'
  
  // Technical
  | 'error_occurred'

  interface EventData {
    // Core context
    page?: string
    user_id?: string
    user_type?: 'parent' | 'teacher'
    brain_state?: string
    app_section?: string
    activity_type?: string
    difficulty?: string
    
    // Learning metrics
    duration?: number
    attempts?: number
    success?: boolean
    
    // Worksheet specific
    worksheet_type?: 'phonics' | 'sight_words' | 'mood_based'
    mood?: string
    pattern?: string
    is_premium?: boolean
    
    // Accessibility context
    accessibility_mode?: string
    tts_used?: boolean
    
    // Technical
    error_message?: string
    error_code?: string
    
    // Generic catch-all
    [key: string]: any
  }

class MinimalAnalytics {
  private analytics: any = null
  private sessionId: string
  private sessionStartTime: number

  constructor() {
    this.sessionId = this.generateSessionId()
    this.sessionStartTime = Date.now()
    this.initFirebase()
  }

  private async initFirebase() {
    if (!ANALYTICS_CONFIG.enabled || !ANALYTICS_CONFIG.firebase) return
    
    // Non-blocking initialization with timeout
    Promise.race([
      isSupported().then(supported => {
        if (supported) {
          this.analytics = getAnalytics(app)
          this.log('Analytics initialized')
        }
      }),
      new Promise(resolve => setTimeout(resolve, 2000)) // 2 second timeout
    ]).catch(error => {
      console.warn('Analytics init failed (non-critical):', error)
    })
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  private log(message: string, data?: any) {
    if (ANALYTICS_CONFIG.console) {
      console.log(`📊 Analytics: ${message}`, data || '')
    }
  }

  // Main tracking method - this is all your components need to call
  track(event: AnalyticsEvent, data: EventData = {}) {
    if (!ANALYTICS_CONFIG.enabled) return

    const eventData = {
      ...data,
      session_id: this.sessionId,
      timestamp: Date.now(),
      session_duration: Date.now() - this.sessionStartTime
    }

    this.log(`Event: ${event}`, eventData)

    // Send to Firebase Analytics
    if (this.analytics && ANALYTICS_CONFIG.firebase) {
      try {
        logEvent(this.analytics, event, eventData)
      } catch (error) {
        console.warn('Firebase Analytics error:', error)
      }
    }

    // Future: Add GA4, Clarity, Sentry here
    // this.sendToGA4(event, eventData)
    // this.sendToClarity(event, eventData) 
    // this.sendToSentry(event, eventData)
  }

  // Convenience methods for common events
  sessionStart(brainState?: string, accessibilityMode?: string) {
    this.track('session_start', {
      brain_state: brainState,
      accessibility_mode: accessibilityMode
    })
  }

  activityStart(activityType: string, appSection: string, difficulty?: string) {
    this.track('activity_start', {
      activity_type: activityType,
      app_section: appSection,
      difficulty
    })
  }

  struggle(context: { attempts?: number; activity?: string; difficulty?: string }) {
    this.track('struggle_moment', {
      attempts: context.attempts,
      activity_type: context.activity,
      difficulty: context.difficulty
    })
  }

  success(context: { duration?: number; activity?: string; difficulty?: string }) {
    this.track('success_moment', {
      duration: context.duration,
      activity_type: context.activity,
      difficulty: context.difficulty,
      success: true
    })
  }

  sessionEnd(totalDuration?: number) {
    this.track('session_end', {
      duration: totalDuration || (Date.now() - this.sessionStartTime)
    })
  }

  // Page view tracking
  pageView(page: string, metadata?: Record<string, any>) {
    this.track('page_view', {
      page,
      ...metadata
    })
  }

  // Worksheet events
  worksheetGenerated(type: string, mood: string, isPremium: boolean) {
    this.track('worksheet_generated', {
      worksheet_type: type,
      mood,
      is_premium: isPremium
    })
  }

  worksheetDownloaded(type: string, mood: string, isPremium: boolean) {
    this.track('worksheet_downloaded', {
      worksheet_type: type,
      mood,
      is_premium: isPremium
    })
  }

  worksheetPreviewOpened(type: string) {
    this.track('worksheet_preview_opened', {
      worksheet_type: type
    })
  }

  // Feature usage
  featureClicked(featureName: string, context?: Record<string, any>) {
    this.track('feature_clicked', {
      feature: featureName,
      ...context
    })
  }

  // Upgrade funnel
  upgradeModalShown(reason: string) {
    this.track('upgrade_modal_shown', {
      reason
    })
  }

  upgradeInitiated(tier: string) {
    this.track('upgrade_initiated', {
      tier
    })
  }

  // Error tracking
  errorOccurred(errorMessage: string, context?: Record<string, any>) {
    this.track('error_occurred', {
      error_message: errorMessage,
      ...context
    })
  }

  // Activity completion
  activityComplete(activityType: string, duration: number, success: boolean) {
    this.track('activity_complete', {
      activity_type: activityType,
      duration,
      success
    })
  }
}

// Export singleton instance
export const analytics = new MinimalAnalytics()

// Export types for components that need them
export type { AnalyticsEvent, EventData }